/**
 * MetadataField - canonical field renderer for the Raven form system.
 *
 * Maps a `FormFieldMeta` to the correct UI component with consistent:
 * - Labels with required markers
 * - Hint / description text
 * - Error display
 * - Input rendering per field type
 *
 * This is the ONLY component that should render backend-driven form fields.
 * The old `@/admin/components/metadata-field.tsx` is deprecated.
 */

import { useEffect, useMemo, useRef, useState } from 'react'

import { Input } from '@/shared/components/ui/input'
import { FileUploader, type UploadedFileItem } from '@/shared/components/ui/file-uploader'
import { humanizeAccept } from '@/shared/lib/file-types'
import {
  ResourceSearchInput,
  type ResourceOption,
} from '@/shared/components/ui/resource-search-input'
import { useResourceLookup } from '@/shared/components/ui/resource-lookup-context'
import { AddressCapture } from '@/shared/components/ui/address-capture'
import {
  toAddressInputValue,
  toMailingAddress,
  isMeaningfulMailingAddress,
} from '@/shared/lib/formatting/address'
import { Label } from '@/shared/components/ui/label'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { AttributeRichText } from '@/shared/components/ui/attribute-rich-text'
import { CurrencyInput } from '@/shared/components/ui/currency-input'
import type { CurrencyValue } from '@/shared/components/ui/currency-input'
import { DatePicker } from '@/shared/components/ui/date-picker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { YesNoToggle } from '@/shared/components/ui/yes-no-toggle'
import { Textarea } from '@/shared/components/ui/textarea'
import { cn } from '@/shared/lib/utils'
import type { FormFieldMeta, SubmissionMode } from '@/shared/lib/forms/types'
import { FILE_FIELD_TYPES, TEXTAREA_FIELD_TYPES, isFieldRequired } from '@/shared/lib/forms/types'
import { shouldDisableFutureDates } from '@/shared/lib/forms/date-constraints'
import { PhoneInput } from '@/shared/components/ui/phone-input'
import { COUNTRY_NAME_OPTIONS, isCountryField } from '@/shared/lib/countries'

export interface MetadataFieldProps {
  /** Field metadata. */
  field: FormFieldMeta
  /** Current field value. */
  value: unknown
  /** Called when the value changes. */
  onChange: (value: unknown) => void
  /** Validation error message. */
  error?: string
  /** Label variant. "default" renders standard label, "compact" renders
   *  uppercase tracking label, "none" hides the label. */
  labelVariant?: 'default' | 'compact' | 'none'
  /** Additional className on the outer wrapper. */
  className?: string
  /** File upload handler. */
  onFileUpload?: (file: File, field: FormFieldMeta) => Promise<{
    uuid: string
    file_name: string
    file_url: string
  }>
  /** Whether a file upload is currently in progress. */
  uploading?: boolean
  /**
   * Allow selecting / dropping more than one file at once. When true the
   * field value is an array of ``{uuid, file_name, file_url}`` objects and
   * the dropzone stays visible so further files can be added. Defaults to
   * false (single-file, backwards compatible).
   */
  multiple?: boolean
  /** Optional onBlur handler (e.g. for email lookup). */
  onBlur?: () => void
  /**
   * Called by an ENTITY_SEARCH field that carries a `prefill_map` in its
   * `configuration_data`, when the user selects a resource. The map's resolved
   * `{ siblingCodeName: value }` pairs are passed so the host form can pre-fill
   * other fields (editable afterwards). No-op for fields without a prefill_map.
   */
  onPrefill?: (values: Record<string, unknown>) => void
  /**
   * The host form's current field values, keyed by code_name. Used by an
   * ENTITY_SEARCH field that declares a `depends_on` + `scope_param` cascade
   * (e.g. the uninstalled-claim product picker scoping to "Product Impacted"):
   * the live value of the `depends_on` sibling is merged into the provider's
   * search params. Optional - fields without a cascade ignore it.
   */
  siblingValues?: Record<string, unknown>
  /**
   * Submission mode for required marker display. The asterisk is shown when
   * the field is required for this mode. Product forms flip this from 'save'
   * to 'publish' on a publish attempt so publish-only fields gain their marker
   * exactly when they become required. Defaults to 'submit' (shows * for all
   * required fields).
   */
  submissionMode?: SubmissionMode
  /**
   * Composite unit-of-measurement companion. When this field declares
   * `configuration_data.uom_field`, the host passes the paired unit field's
   * live state here and MetadataField renders the unit `Select` (minor, left)
   * inline with the number input (major, right) - the same layout as the
   * generated form's quantity+UOM composite. Opt-in: without it a NUMBER field
   * renders as a plain number input. The host is responsible for hiding the
   * companion's own standalone `MetadataField` (its
   * `configuration_data.rendered_inline_by` names this field).
   */
  companion?: {
    options: { value: string; label: string }[]
    value: unknown
    onChange: (value: unknown) => void
    error?: string
    placeholder?: string
  }
}

export function MetadataField({
  field,
  value,
  onChange,
  error,
  labelVariant = 'default',
  className,
  onFileUpload,
  uploading = false,
  multiple = false,
  onBlur,
  submissionMode = 'submit',
  onPrefill,
  companion,
  siblingValues,
}: MetadataFieldProps) {
  const ft = field.field_type.toUpperCase()
  const required = isFieldRequired(field, submissionMode)

  const inputClassName = cn('text-[13px]', error && 'border-destructive')

  const errorEl = error ? (
    <p className="mt-1 text-xs text-[var(--status-error)]">{error}</p>
  ) : null

  const hintEl =
    field.description && !error ? (
      <p className="mt-1 text-xs text-[var(--ink-3)]">{field.description}</p>
    ) : field.hint && !error ? (
      <p className="mt-1 text-xs text-[var(--ink-3)]">{field.hint}</p>
    ) : null

  // The asterisk reflects whether the field is required for the CURRENT
  // submission mode. Product forms flip `submissionMode` from 'save' to
  // 'publish' when the user attempts to publish, so a publish-only field gains
  // its marker exactly when it becomes required ("required-when").
  const requiredMarker = required ? (
    <span className="ml-0.5 align-middle text-[13px] leading-none text-[var(--status-error)]">*</span>
  ) : null

  const labelEl =
    labelVariant === 'none' ? null : labelVariant === 'compact' ? (
      <Label className="gap-0 mb-1.5 block text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
        {field.display_name}
        {requiredMarker}
      </Label>
    ) : (
      <Label className="gap-0 clara-label mb-1.5 block font-normal">
        {field.display_name}
        {requiredMarker}
      </Label>
    )

  // ── File uploads ──
  if (FILE_FIELD_TYPES.has(ft)) {
    // Multi-file capture is driven by ``file_multiple`` - the canonical key the
    // backend stamps on CLAIM_EVIDENCE / multi-file attributes. Read both the
    // first-class ``FormFieldMeta.file_multiple`` (which normalisers such as
    // ``normaliseClaimField`` populate) AND the raw
    // ``configuration_data.file_multiple`` (for callers that pass config
    // through un-normalised), mirroring how the generated-form pipeline and the
    // workflow-action-dialog resolve `multiple`. Honour it here so every
    // MetadataField caller renders the shared FileUploader in multi mode without
    // threading a `multiple` prop. The OC claim action dialog (Submit Documents)
    // rendered capture fields through MetadataField without passing the prop, so
    // it fell back to the `false` default and forced single-file even though the
    // field config asked for multiple. An explicit `multiple` prop still wins.
    const fileMultiple =
      multiple ||
      Boolean(field.file_multiple) ||
      Boolean(
        (field.configuration_data as Record<string, unknown> | undefined)
          ?.file_multiple,
      )
    return (
      <FileField
        field={field}
        value={value}
        onChange={onChange}
        error={error}
        labelEl={labelEl}
        errorEl={errorEl}
        hintEl={hintEl}
        onFileUpload={onFileUpload}
        uploading={uploading}
        multiple={fileMultiple}
        className={className}
      />
    )
  }

  // ── Boolean / Checkbox ──
  if (ft === 'BOOLEAN') {
    const displayAs = (field.configuration_data as Record<string, unknown>)?.display_as
    // Disclaimer: a notice (title + long copy) with the acknowledgement
    // checkbox rendered as its final line, all as one config-driven unit
    // (opt-in via configuration_data.display_as === 'disclaimer'). Copy comes
    // from configuration_data.disclaimer_title / disclaimer_text (or the
    // attribute description as a fallback); the checkbox label is the field's
    // display_name. Must be checked when required (isRequiredValueProvided).
    if (displayAs === 'disclaimer') {
      const config = (field.configuration_data ?? {}) as Record<string, unknown>
      const title = typeof config.disclaimer_title === 'string' ? config.disclaimer_title : ''
      const text =
        typeof config.disclaimer_text === 'string'
          ? config.disclaimer_text
          : (field.description ?? '')
      // The checkbox label supports Markdown (links/emphasis) via a dedicated
      // `checkbox_label` config key so `display_name` stays a short field name
      // (used as the record's card label). Falls back to the plain
      // `display_name` when no rich label is authored.
      const checkboxLabel =
        typeof config.checkbox_label === 'string' && config.checkbox_label.trim() !== ''
          ? config.checkbox_label
          : null
      return (
        <div className={className}>
          <div
            className={cn(
              'rounded-[8px] border-[0.5px] p-4',
              error ? 'border-[var(--status-error)]' : 'border-[var(--rule)]',
            )}
          >
            {title && (
              <p className="m-0 mb-1.5 text-[12px] font-medium text-[var(--ink-1)]">{title}</p>
            )}
            {text && (
              <AttributeRichText
                content={text}
                className="m-0 text-[12px] leading-[1.6] text-[var(--ink-2)]"
              />
            )}
            <Label className="mt-3 flex cursor-pointer items-start gap-2 font-normal">
              <Checkbox
                checked={!!value && value !== 'false'}
                onCheckedChange={(checked) => onChange(checked)}
                aria-invalid={error ? true : undefined}
                aria-required={isFieldRequired(field, submissionMode) || undefined}
                className={cn(
                  'mt-0.5 cursor-pointer',
                  error && 'border-[var(--status-error)]',
                )}
              />
              <span className="text-[12px] leading-[1.5] text-[var(--ink-1)]">
                {checkboxLabel ? (
                  <AttributeRichText content={checkboxLabel} variant="inline" />
                ) : (
                  field.display_name
                )}
                {isFieldRequired(field, submissionMode) && (
                  <span className="ml-0.5 text-[var(--status-error)]">*</span>
                )}
              </span>
            </Label>
          </div>
          {errorEl}
        </div>
      )
    }
    // Explicit Yes/No control (opt-in via configuration_data.display_as).
    // Unlike a checkbox, this lets the user affirmatively answer "No" and
    // satisfy a required field, rather than an unticked box reading as
    // "unanswered". Neither option is preselected, so a required field forces
    // an explicit True/False choice.
    if ((field.configuration_data as Record<string, unknown>)?.display_as === 'yes_no') {
      const yesNoValue =
        value === true || value === 'true' || value === 1
          ? true
          : value === false || value === 'false'
            ? false
            : null
      return (
        <div className={className}>
          <div className="flex items-center justify-between gap-4">
            <span className="text-[13px] text-[var(--ink-2)]">
              {field.display_name}
              {isFieldRequired(field, submissionMode) && (
                <span className="ml-0.5 text-[var(--status-error)]">*</span>
              )}
            </span>
            <YesNoToggle value={yesNoValue} onChange={onChange} />
          </div>
          {field.description && (
            <p className="mt-1 text-[10px] text-[var(--ink-3)]">{field.description}</p>
          )}
          {errorEl}
        </div>
      )
    }
    return (
      <div className={className}>
        <Label className="flex cursor-pointer items-center gap-2.5 font-normal">
          <Checkbox
            checked={!!value && value !== 'false'}
            onCheckedChange={(checked) => onChange(checked)}
            className="cursor-pointer"
          />
          <span className="text-[13px] text-[var(--ink-2)]">{field.display_name}</span>
        </Label>
        {field.description && (
          <p className="ml-6 mt-0.5 text-[10px] text-[var(--ink-3)]">{field.description}</p>
        )}
        {errorEl}
      </div>
    )
  }

  // ── Select / Dropdown ──
  if (ft === 'SELECT' || ft === 'DROPDOWN') {
    const options = resolveOptions(field)
    return (
      <div className={className}>
        {labelEl}
        <Select
          value={(value as string) ?? ''}
          onValueChange={(v) => onChange(v || null)}
          disabled={field.disabled}
        >
          <SelectTrigger
            size="sm"
            className={cn('w-full text-[13px]', error && 'border-destructive')}
          >
            <SelectValue
              placeholder={field.hint || `Select ${field.display_name.toLowerCase()}`}
            />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errorEl}
        {/* A disabled select carries its guidance in the placeholder, so skip
            the duplicate hint line below it. */}
        {!field.disabled && hintEl}
      </div>
    )
  }

  // ── Multi-select ──
  if (ft === 'MULTI_SELECT') {
    const options = resolveOptions(field)
    const selected = Array.isArray(value) ? (value as string[]) : []
    function toggleOption(optValue: string) {
      if (selected.includes(optValue)) {
        onChange(selected.filter((v) => v !== optValue))
      } else {
        onChange([...selected, optValue])
      }
    }
    return (
      <div className={className}>
        {labelEl}
        <div
          className={cn(
            'flex flex-col gap-1 rounded-[4px] border border-input bg-transparent p-2',
            error && 'border-destructive',
          )}
        >
          {options.length === 0 && (
            <p className="py-2 text-center text-xs text-[var(--ink-3)]">No options available</p>
          )}
          {options.map((opt) => {
            const isChecked = selected.includes(opt.value)
            return (
              <Label
                key={opt.value}
                className={cn(
                  'flex cursor-pointer items-center gap-2.5 rounded-[4px] px-2 py-2 font-normal transition-colors',
                  isChecked ? 'bg-[var(--green-bg)]' : 'bg-transparent',
                )}
              >
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={() => toggleOption(opt.value)}
                  className="cursor-pointer"
                />
                <span className="text-[13px] text-[var(--ink-2)]">{opt.label}</span>
              </Label>
            )
          })}
        </div>
        {errorEl}
      </div>
    )
  }

  // ── Money / Currency ──
  // MONEY / CURRENCY persists as ``{amount, currency}`` dicts; we
  // delegate to the shared ``CurrencyInput`` so the same component
  // serves both the useForm (CurrencyField) and MetadataField paths.
  if (ft === 'MONEY' || ft === 'CURRENCY') {
    const moneyOptions =
      (field.configuration_data?.options as
        | { value: string; label: string }[]
        | undefined) ?? undefined
    return (
      <div className={className}>
        {labelEl}
        <CurrencyInput
          value={(value as CurrencyValue | null | undefined) ?? null}
          currencyOptions={moneyOptions}
          onChange={(next) => onChange(next)}
          placeholder={field.hint || 'Amount'}
          disabled={field.disabled}
          onBlur={onBlur}
          hasError={!!error}
        />
        {errorEl}
        {hintEl}
      </div>
    )
  }

  // ── Number / Decimal ──
  if (ft === 'NUMBER' || ft === 'DECIMAL') {
    const uomField = (field.configuration_data as Record<string, unknown> | undefined)
      ?.uom_field
    // Composite quantity + unit-of-measurement: the unit `Select` (minor) on
    // the left, the number (major) on the right - the same layout as the
    // generated form's QuantityUomField and the phone/currency inputs. Opt-in
    // and config-driven: only when the field declares `uom_field` and the host
    // supplies the paired unit state via `companion`.
    if (uomField && companion) {
      const uomPlaceholder = companion.placeholder || 'Unit'
      // Always keep the stored unit visible even if the current (cascaded)
      // options don't include it - so a saved value never silently vanishes on
      // a surface whose cascade parent isn't in scope.
      const uomVal = companion.value as string | undefined
      const uomOptions =
        uomVal && !companion.options.some((o) => o.value === uomVal)
          ? [
              ...companion.options,
              { value: uomVal, label: uomVal.charAt(0).toUpperCase() + uomVal.slice(1) },
            ]
          : companion.options
      return (
        <div className={className}>
          {labelEl}
          <div className="flex items-stretch gap-2">
            <div className="w-[130px] shrink-0">
              <Select
                value={(companion.value as string) ?? ''}
                onValueChange={(v) => companion.onChange(v || null)}
                disabled={field.disabled}
              >
                <SelectTrigger
                  size="sm"
                  aria-label={uomPlaceholder}
                  className={cn(
                    'w-full text-[13px]',
                    companion.error && 'border-destructive',
                  )}
                >
                  <SelectValue placeholder={uomPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {uomOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Input
              type="number"
              step={ft === 'DECIMAL' ? '0.01' : 'any'}
              value={(value as string) ?? ''}
              onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
              placeholder={field.hint || ''}
              className={cn(inputClassName, 'flex-1')}
              disabled={field.disabled}
              onBlur={onBlur}
            />
          </div>
          {error || companion.error ? (
            <p className="mt-1 text-xs text-[var(--status-error)]">
              {error || companion.error}
            </p>
          ) : (
            hintEl
          )}
        </div>
      )
    }
    return (
      <div className={className}>
        {labelEl}
        <Input
          type="number"
          step={ft === 'DECIMAL' ? '0.01' : 'any'}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
          placeholder={field.hint || ''}
          className={inputClassName}
          disabled={field.disabled}
          onBlur={onBlur}
        />
        {errorEl}
        {hintEl}
      </div>
    )
  }

  // ── Date ──
  if (ft === 'DATE') {
    // Past-only date fields (incident / purchase / loss) block future dates.
    // Config-driven via `disable_future` / `allow_future`, with a default
    // allowlist of past-only code_names - see `shouldDisableFutureDates`.
    const disableFuture = shouldDisableFutureDates(
      field.code_name,
      field.configuration_data as Record<string, unknown> | undefined,
    )
    return (
      <div className={className}>
        {labelEl}
        <DatePicker
          value={(value as string) ?? ''}
          onChange={(v) => onChange(v || null)}
          disableFuture={disableFuture}
          hasError={!!error}
        />
        {errorEl}
        {hintEl}
      </div>
    )
  }

  // ── DateTime ──
  if (ft === 'DATE_TIME') {
    return (
      <div className={className}>
        {labelEl}
        <Input
          type="datetime-local"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          className={inputClassName}
          disabled={field.disabled}
          onBlur={onBlur}
        />
        {errorEl}
        {hintEl}
      </div>
    )
  }

  // ── Textarea / Long Text / Rich Text ──
  if (TEXTAREA_FIELD_TYPES.has(ft)) {
    return (
      <div className={className}>
        {labelEl}
        <Textarea
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          disabled={field.disabled}
          placeholder={field.hint || ''}
          onBlur={onBlur}
          className={cn('resize-y text-[13px]', error && 'border-destructive')}
        />
        {errorEl}
        {hintEl}
      </div>
    )
  }

  // ── Country (any text-typed field whose code_name / maps_to_column is
  // a country reference). Rendered as a Select so users get a curated
  // list aligned with address-lookup output. Done before the default
  // text branch so it overrides plain-TEXT country attributes set up in
  // tenant templates without requiring a SELECT field_type. ──
  if (
    isCountryField(field) &&
    ft !== 'NUMBER' &&
    ft !== 'BOOLEAN' &&
    ft !== 'DATE' &&
    ft !== 'DATE_TIME' &&
    ft !== 'MULTI_SELECT'
  ) {
    const currentValue = (value as string) ?? ''
    // Preserve unknown values (e.g. legacy free-text input from before
    // this control existed) by appending them as an extra option so the
    // trigger can still show the saved value instead of going blank.
    const knownValues = new Set(COUNTRY_NAME_OPTIONS.map((o) => o.value))
    const options =
      currentValue && !knownValues.has(currentValue)
        ? [...COUNTRY_NAME_OPTIONS, { value: currentValue, label: currentValue }]
        : COUNTRY_NAME_OPTIONS
    return (
      <div className={className}>
        {labelEl}
        <Select
          value={currentValue}
          onValueChange={(v) => onChange(v || null)}
        >
          <SelectTrigger
            size="sm"
            className={cn('w-full text-[13px]', error && 'border-destructive')}
          >
            <SelectValue
              placeholder={field.hint || `Select ${field.display_name.toLowerCase()}`}
            />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errorEl}
        {hintEl}
      </div>
    )
  }

  // ── Phone - shared <PhoneInput> (flag selector + libphonenumber-js) ──
  if (ft === 'PHONE') {
    return (
      <div className={className}>
        {labelEl}
        <PhoneInput
          value={(value as string) ?? ''}
          onChange={(next) => onChange(next || null)}
          onBlur={onBlur}
          disabled={field.disabled}
          hasError={!!error}
          placeholder={field.hint || ''}
        />
        {errorEl}
        {hintEl}
      </div>
    )
  }

  // ── Entity search (searchable resource picker) ──
  // Resolves options through a resource-lookup provider keyed by
  // `configuration_data.lookup_provider`. Generic: any "search and pick a
  // related resource" field (product, contractor, customer, …) uses this.
  if (ft === 'ENTITY_SEARCH') {
    return (
      <div className={className}>
        {labelEl}
        <EntitySearchField
          field={field}
          value={value}
          onChange={onChange}
          onPrefill={onPrefill}
          hasError={!!error}
          siblingValues={siblingValues}
        />
        {errorEl}
        {hintEl}
      </div>
    )
  }

  // ── Address (composite lookup + manual entry) ──
  // The one platform-wide ADDRESS form renderer: renders the shared
  // `AddressCapture` (search-first with autocomplete + a labeled manual-entry
  // modal + single-line summary) - identical to the registration wizard.
  // Display side lives in formatDisplayValue's `address` case.
  if (ft === 'ADDRESS') {
    return (
      <div className={className}>
        {labelEl}
        <AddressMetadataField
          field={field}
          value={value}
          onChange={onChange}
          error={error}
        />
        {errorEl}
        {hintEl}
      </div>
    )
  }

  // ── Free-text with a type-ahead over prior values ──
  // A text-like field that declares `configuration_data.autocomplete_source`
  // (WPS-164 #15: Plant / Production Code) renders a datalist-backed input -
  // free text plus suggestions from a resource-lookup provider, scoped to the
  // field's code_name. Generic: any such attribute opts in via config.
  if (
    (ft === 'TEXT' || ft === 'EMAIL' || ft === 'LINK') &&
    typeof (field.configuration_data as Record<string, unknown> | undefined)
      ?.autocomplete_source === 'string'
  ) {
    return (
      <div className={className}>
        {labelEl}
        <AttributeHistoryField
          field={field}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          inputClassName={inputClassName}
        />
        {errorEl}
        {hintEl}
      </div>
    )
  }

  // ── Default: text input (TEXT, EMAIL, LINK, etc.) ──
  return (
    <div className={className}>
      {labelEl}
      <Input
        type={ft === 'EMAIL' ? 'email' : 'text'}
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        placeholder={field.hint || ''}
        className={inputClassName}
        disabled={field.disabled}
        onBlur={onBlur}
      />
      {errorEl}
      {hintEl}
    </div>
  )
}

// ── Attribute-history type-ahead sub-component ──────────
// Renders a free-text input with a native <datalist> of suggestions fetched
// from the provider named by `configuration_data.autocomplete_source`, scoped
// to this field's code_name. The stored value is the plain string typed or
// picked (free text is always allowed). Kept as its own component so the
// provider + debounce hooks only mount for autocomplete fields.
function AttributeHistoryField({
  field,
  value,
  onChange,
  onBlur,
  inputClassName,
}: {
  field: FormFieldMeta
  value: unknown
  onChange: (value: unknown) => void
  onBlur?: () => void
  inputClassName?: string
}) {
  const cd = field.configuration_data ?? {}
  const source = cd.autocomplete_source as string | undefined
  const provider = useResourceLookup(source)
  const [suggestions, setSuggestions] = useState<string[]>([])
  const current = (value as string) ?? ''
  const listId = `${field.code_name}-history`

  useEffect(() => {
    if (!provider) return
    let cancelled = false
    const handle = setTimeout(() => {
      provider
        .search(current, { code_name: field.code_name })
        .then((opts) => {
          if (cancelled) return
          const names = (opts ?? [])
            .map((o) => o.name)
            .filter((n): n is string => typeof n === 'string' && n.length > 0)
          setSuggestions(Array.from(new Set(names)).slice(0, 20))
        })
        .catch(() => {
          if (!cancelled) setSuggestions([])
        })
    }, 200)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [provider, current, field.code_name])

  return (
    <>
      <Input
        type="text"
        list={listId}
        value={current}
        onChange={(e) => onChange(e.target.value || null)}
        placeholder={field.hint || ''}
        className={inputClassName}
        disabled={field.disabled}
        onBlur={onBlur}
      />
      <datalist id={listId}>
        {suggestions.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
    </>
  )
}

// ── Entity search sub-component ─────────────────────────
// Kept as its own component so the provider lookup + results state hooks are
// only mounted for ENTITY_SEARCH fields. The selected value is stored as a
// `{ uuid, name }` object (the picker's natural shape).
function EntitySearchField({
  field,
  value,
  onChange,
  onPrefill,
  hasError = false,
  siblingValues,
}: {
  field: FormFieldMeta
  value: unknown
  onChange: (value: unknown) => void
  onPrefill?: (values: Record<string, unknown>) => void
  hasError?: boolean
  siblingValues?: Record<string, unknown>
}) {
  const cd = field.configuration_data ?? {}
  const providerKey = cd.lookup_provider as string | undefined
  // Optional `{ siblingCodeName: "dotted.path.into.selected.record" }` map. On
  // select, each path is resolved against the picked option's raw `data` and
  // handed to `onPrefill` so the host form can fill those sibling fields.
  const prefillMap = cd.prefill_map as Record<string, string> | undefined
  const iconName = (cd.icon_name as string) ?? (cd.icon as string) ?? 'package'
  const placeholder = field.display_name ?? (cd.placeholder as string) ?? undefined
  const provider = useResourceLookup(providerKey)

  const [results, setResults] = useState<ResourceOption[]>([])
  const latestSearchRef = useRef('')

  const staticParams = cd.provider_params as Record<string, unknown> | undefined
  // Live cascade: when the field declares `depends_on` + `scope_param`, merge
  // the current value of the sibling field into the provider params so results
  // narrow as that sibling changes (e.g. "Product Impacted" scopes the product
  // search to that category). `scope_map` optionally translates the sibling
  // value (e.g. `roofing` -> `shingle-lines`) before it is sent. Generic: only
  // active when both keys are present, so every other ENTITY_SEARCH field is
  // unaffected. Mirrors ResourceSearchField in shared/lib/forms/generated-input.
  const dependsOn = cd.depends_on as string | undefined
  const scopeParam = cd.scope_param as string | undefined
  const scopeMap = cd.scope_map as Record<string, string> | undefined
  const siblingValue = dependsOn ? siblingValues?.[dependsOn] : undefined
  const providerParams = useMemo(() => {
    if (!scopeParam || siblingValue == null || siblingValue === '') {
      return staticParams
    }
    // The sibling may itself be an ENTITY_SEARCH value ({ uuid, name }); use
    // its uuid so `String(...)` never collapses to "[object Object]".
    const raw =
      typeof siblingValue === 'object' &&
      siblingValue !== null &&
      'uuid' in siblingValue
        ? String((siblingValue as { uuid: unknown }).uuid)
        : String(siblingValue)
    const mapped = scopeMap ? scopeMap[raw] : raw
    if (!mapped) return staticParams
    return { ...(staticParams ?? {}), [scopeParam]: mapped }
  }, [scopeParam, scopeMap, siblingValue, staticParams])
  const paramsKey = JSON.stringify(providerParams ?? null)
  // The scope (paramsKey) whose full, unfiltered list came back empty - e.g. a
  // product with no colours.
  const [emptyScopeKey, setEmptyScopeKey] = useState<string | null>(null)

  useEffect(() => {
    if (!provider) return
    let cancelled = false
    provider
      .search('', providerParams)
      .then((opts) => {
        if (cancelled) return
        setEmptyScopeKey((opts ?? []).length === 0 ? paramsKey : null)
        if (latestSearchRef.current === '') setResults(opts ?? [])
      })
      .catch(() => {})
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, paramsKey])

  // Mirrors ResourceSearchField. A pick scoped by its sibling (e.g. a colour
  // of the chosen product) stops being valid when that sibling changes, so
  // clear it. Skips the first render so a value already chosen survives the
  // field mounting.
  const isBlank = value == null || value === ''
  const scopeKey = scopeParam ? paramsKey : null
  const lastScopeKey = useRef(scopeKey)
  useEffect(() => {
    if (lastScopeKey.current === scopeKey) return
    lastScopeKey.current = scopeKey
    if (!isBlank) onChange(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey])

  // WPS-270: when the sibling-scoped list is empty, record
  // `configuration_data.empty_value` (e.g. "NA") instead of a blank, never
  // over a chosen value.
  const emptyValue = cd.empty_value as string | undefined
  const scoped = Boolean(scopeParam && providerParams?.[scopeParam])
  useEffect(() => {
    if (!emptyValue || !scoped || emptyScopeKey !== paramsKey || !isBlank) return
    onChange({ uuid: '', name: emptyValue })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emptyValue, scoped, emptyScopeKey, paramsKey, isBlank])

  const handleSearch = (term: string) => {
    if (!provider) {
      setResults([])
      return
    }
    latestSearchRef.current = term
    provider
      .search(term, providerParams)
      .then((opts) => {
        if (latestSearchRef.current === term) setResults(opts)
      })
      .catch(() => {
        if (latestSearchRef.current === term) setResults([])
      })
  }

  // Value is normally { uuid, name }, but a persisted value can come back as
  // a bare UUID string. Normalise defensively so ResourceSearchInput never
  // receives a malformed value and crashes.
  const selected =
    value && typeof value === 'object' && 'uuid' in value
      ? (value as { uuid: string; name: string })
      : typeof value === 'string' && value
        ? { uuid: value, name: value }
        : null

  const handleSelect = (next: { uuid: string; name: string } | null) => {
    onChange(next)
    if (!next || !prefillMap || !onPrefill) return
    const picked = results.find((r) => r.uuid === next.uuid)
    if (!picked?.data) return
    const resolved: Record<string, unknown> = {}
    for (const [codeName, path] of Object.entries(prefillMap)) {
      resolved[codeName] = resolvePath(picked.data, path)
    }
    onPrefill(resolved)
  }

  return (
    <ResourceSearchInput
      value={selected}
      onChange={handleSelect}
      results={results}
      onSearch={handleSearch}
      iconName={iconName}
      searchPlaceholder={placeholder ?? 'Search...'}
      emptyMessage={(cd.empty_message as string) ?? (field.hint as string) ?? 'No results found'}
      hasError={hasError}
    />
  )
}

/** Resolve a dotted path (e.g. "attribute_summary.email") into a nested record,
 *  returning "" when any segment is missing so a prefill never writes
 *  ``undefined`` into a form field. */
function resolvePath(source: Record<string, unknown>, path: string): unknown {
  let cur: unknown = source
  for (const segment of path.split('.')) {
    if (cur && typeof cur === 'object' && segment in (cur as object)) {
      cur = (cur as Record<string, unknown>)[segment]
    } else {
      return ''
    }
  }
  return cur ?? ''
}

// ── Address sub-component ──────────────────────────────
// Renders the shared `AddressCapture` (search + labeled manual modal + summary)
// so ADDRESS looks identical to the registration wizard everywhere. Stores the
// backend-compatible camelCase shape; the display side normalises any shape via
// `addressDisplay`.
function AddressMetadataField({
  field,
  value,
  onChange,
  error,
}: {
  field: FormFieldMeta
  value: unknown
  onChange: (value: unknown) => void
  error?: string
}) {
  const lookupProvider = field.configuration_data?.lookup_provider as
    | string
    | undefined
  const defaultCountry = field.configuration_data?.default_country as
    | string
    | undefined

  const current = toMailingAddress(value)

  return (
    <AddressCapture
      value={isMeaningfulMailingAddress(current) ? current : null}
      // Store the backend-compatible camelCase shape; AddressCapture is UI only.
      onChange={(next) => onChange(next ? toAddressInputValue(next) : null)}
      lookupProvider={lookupProvider}
      defaultCountry={defaultCountry}
      error={error}
      // MetadataField already renders the field label above.
      summaryLabel=""
    />
  )
}

// ── File upload sub-component ──────────────────────────

// DOCUMENT covers receipts / proof-of-purchase, which are very commonly a
// phone PHOTO (PNG/JPG) rather than a scanned PDF. Accept standard image
// formats alongside PDF/Word so the picker never rejects a receipt photo.
// Mirrors the backend PROOF_OF_PURCHASE allow-list in
// backend/apps/files/validation.py::ALLOWED_MIME_TYPES_BY_USE_CASE.
const FILE_ACCEPT_MAP: Record<string, string> = {
  IMAGE: 'image/*',
  VIDEO: 'video/*',
  DOCUMENT: 'image/png,image/jpeg,.png,.jpg,.jpeg,application/pdf,.pdf,.doc,.docx',
  FILE: '*/*',
}

type UploadedFileValue = { uuid: string; file_name: string; file_url: string }

function isUploadedFile(v: unknown): v is UploadedFileValue {
  return !!v && typeof v === 'object' && typeof (v as UploadedFileValue).uuid === 'string'
}

function FileField({
  field,
  value,
  onChange,
  error,
  labelEl,
  errorEl,
  hintEl,
  onFileUpload,
  multiple = false,
  className,
}: {
  field: FormFieldMeta
  value: unknown
  onChange: (v: unknown) => void
  error?: string
  labelEl: React.ReactNode
  errorEl: React.ReactNode
  hintEl: React.ReactNode
  onFileUpload?: MetadataFieldProps['onFileUpload']
  uploading: boolean
  multiple?: boolean
  className?: string
}) {
  const ft = field.field_type.toUpperCase()
  const accept = FILE_ACCEPT_MAP[ft] || '*/*'
  const acceptLabel = humanizeAccept(accept)

  // Adapt the stored value to the shared uploader's always-array shape.
  // Single-file mode keeps emitting a bare object via ``onChange``
  // (backwards compatible); multiple mode emits an array of objects.
  const items: UploadedFileItem[] = multiple
    ? Array.isArray(value)
      ? value.filter(isUploadedFile)
      : []
    : isUploadedFile(value)
      ? [value]
      : []

  const uploadFn = async (file: File): Promise<UploadedFileItem> => {
    if (!onFileUpload) throw new Error('No file upload handler configured')
    const r = await onFileUpload(file, field)
    return { uuid: r.uuid, file_name: r.file_name, file_url: r.file_url }
  }

  const handleChange = (files: UploadedFileItem[]) => {
    const toValue = (f: UploadedFileItem): UploadedFileValue => ({
      uuid: f.uuid,
      file_name: f.file_name,
      file_url: f.file_url ?? '',
    })
    if (multiple) {
      onChange(files.map(toValue))
    } else {
      onChange(files[0] ? toValue(files[0]) : null)
    }
  }

  return (
    <div className={className}>
      {labelEl}
      <FileUploader
        value={items}
        onChange={handleChange}
        uploadFn={uploadFn}
        multiple={multiple}
        accept={accept}
        acceptLabel={acceptLabel || undefined}
        hint={field.hint || undefined}
        error={!!error}
        disabled={!onFileUpload}
      />
      {hintEl}
      {errorEl}
    </div>
  )
}

// ── Helpers ─────────────────────────────────────────────

function resolveOptions(field: FormFieldMeta): { value: string; label: string }[] {
  if (field.options && field.options.length > 0) {
    return field.options
  }
  const cdOptions = field.configuration_data?.options
  if (Array.isArray(cdOptions)) {
    return cdOptions.map((opt: { value: string; label?: string }) => ({
      value: opt.value,
      label:
        opt.label ||
        opt.value
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase()),
    }))
  }
  return []
}
